// Static landing sections (timeline, coaching, trust) — extracted from pages/Auth.jsx.
import { motion } from 'framer-motion';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle2, ChevronDown, Clock, Shield, Zap } from 'lucide-react';
import { LiveDemoCarousel } from '../LiveDemoCarousel';
import { NANOUK } from '../../pages/authContent';

export function LandingSections({ t, language, openAuth }) {
    return (
        <>
            {/* ==================== A DAY WITH NANOUK TIMELINE ==================== */}
            <section id="day-section" className="py-20 px-4 bg-background">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="font-display text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-primary bg-clip-text text-transparent">
                            {t.dayTitle}
                        </h2>
                        <p className="text-muted-foreground text-lg">{t.daySubtitle}</p>
                    </motion.div>

                    <div className="relative">
                        {/* Timeline Line */}
                        <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-primary" />

                        {/* Timeline Items */}
                        {t.timeline.map((item, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className={`relative flex items-center gap-4 md:gap-8 mb-12 ${
                                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                                }`}
                            >
                                {/* Time Badge */}
                                <div className="absolute left-0 md:left-1/2 md:-translate-x-1/2 z-10">
                                    <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${item.color} flex items-center justify-center shadow-lg`}>
                                        <item.icon className="w-7 h-7 text-foreground" />
                                    </div>
                                </div>

                                {/* Content Card */}
                                <div className={`ml-24 md:ml-0 md:w-[calc(50%-4rem)] ${index % 2 === 0 ? 'md:pr-8' : 'md:pl-8'}`}>
                                    <Card className="bg-card border-border backdrop-blur overflow-hidden">
                                        <CardContent className="p-5">
                                            <div className="flex items-start gap-4">
                                                <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 border-2 border-border">
                                                    <img 
                                                        src={NANOUK[item.nanoukImg]}
                                                        alt="Nanouk"
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <Badge variant="outline" className="text-xs border-border text-muted-foreground">
                                                            <Clock className="w-3 h-3 mr-1" />
                                                            {item.time}
                                                        </Badge>
                                                    </div>
                                                    <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                                                    <p className="text-sm text-muted-foreground">{item.description}</p>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==================== NANOUK'S CONCRETE COACHING (P1) ==================== */}
            <section className="py-20 px-4 bg-secondary/30">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <h2 className="font-display text-3xl md:text-4xl font-bold mb-3 text-foreground">
                            {language === 'de' ? 'In Aktion sehen' : 'See it in action'}
                        </h2>
                        <p className="text-muted-foreground text-lg">
                            {language === 'de'
                                ? 'Drei Schritte — ein Tag mit Nanouk in 30 Sekunden.'
                                : 'Three steps — a day with Nanouk in 30 seconds.'}
                        </p>
                    </motion.div>
                    <LiveDemoCarousel isDe={language === 'de'} />
                </div>
            </section>

            {/* ==================== NANOUK'S CONCRETE COACHING SECTION ==================== */}
            <section className="py-20 px-4 bg-background">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <h2 className="font-display text-3xl md:text-4xl font-bold mb-3 text-foreground">
                            {t.coachingTitle}
                        </h2>
                        <p className="text-muted-foreground text-lg">{t.coachingSubtitle}</p>
                    </motion.div>

                    <div className="grid md:grid-cols-3 gap-4">
                        {t.coachingItems.map((item, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.08 }}
                            >
                                <Card
                                    className="bg-card border-border h-full hover:border-cyan-500/40 transition-colors"
                                    data-testid={`coaching-item-${i}`}
                                >
                                    <CardContent className="p-5 space-y-2">
                                        <div className="text-sm font-semibold text-cyan-600 dark:text-cyan-400">
                                            {item.time}
                                        </div>
                                        <h3 className="font-display text-lg font-bold leading-tight">
                                            {item.title}
                                        </h3>
                                        <p className="text-sm text-muted-foreground leading-relaxed">
                                            {item.description}
                                        </p>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==================== QUALITY PROMISE / TRUST SECTION ==================== */}
            <section className="py-20 px-4 bg-background">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <div className="w-24 h-24 mx-auto mb-6 rounded-2xl overflow-hidden shadow-xl">
                            <img src={NANOUK.smart} alt="Nanouk Expert" className="w-full h-full object-cover" />
                        </div>
                        <h2 className="font-display text-3xl md:text-4xl font-bold mb-4 text-foreground">
                            {t.trustTitle}
                        </h2>
                        <p className="text-xl text-muted-foreground italic mb-4">
                            &ldquo;{t.trustSubtitle}&rdquo;
                        </p>
                        <Badge className="bg-gradient-to-r from-cyan-500 to-blue-600 text-foreground border-0 px-4 py-1">
                            <Shield className="w-4 h-4 mr-2" />
                            {t.trustBadge}
                        </Badge>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="grid md:grid-cols-3 gap-6"
                    >
                        {t.trustPoints.map((point, index) => (
                            <Card key={index} className="bg-card border-border backdrop-blur">
                                <CardContent className="p-6 flex items-start gap-3">
                                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                                    <p className="text-muted-foreground">{point}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </motion.div>

                    {/* Privacy / data-trust mini-block (P0) */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="mt-10"
                        data-testid="trust-privacy-block"
                    >
                        <div className="max-w-3xl mx-auto rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/5 via-card to-cyan-500/5 p-6">
                            <div className="flex items-center gap-2 mb-4">
                                <Shield className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                                <h3 className="font-semibold text-base">{t.trustPrivacyTitle}</h3>
                            </div>
                            <div className="grid sm:grid-cols-3 gap-3">
                                {t.trustPrivacyPoints.map((p, i) => (
                                    <div
                                        key={i}
                                        className="flex items-start gap-2 text-sm text-muted-foreground"
                                        data-testid={`privacy-point-${i}`}
                                    >
                                        <span className="text-lg leading-none mt-0.5" aria-hidden="true">{p.emoji}</span>
                                        <span className="leading-snug">{p.label}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 text-center">
                                <a
                                    href="/legal#privacy"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                                    data-testid="trust-read-more-btn"
                                >
                                    {t.trustReadMore}
                                    <ChevronDown className="w-3 h-3 -rotate-90" />
                                </a>
                            </div>
                        </div>
                    </motion.div>

                    {/* Final CTA */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mt-12"
                    >
                        <Button
                            size="lg"
                            onClick={() => openAuth('register')}
                            className="text-lg px-8 py-6 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full shadow-lg shadow-primary/30 gap-2"
                            data-testid="final-cta-register-btn"
                        >
                            <Zap className="w-5 h-5" />
                            {t.heroCta}
                        </Button>
                    </motion.div>
                </div>
            </section>

        </>
    );
}
